/*!
 * @copyright
 * Copyright(c) 2018 SICK AG
 */
import { CrownCallFactory } from '../CrownCallFactory';
import { CrownCom } from '../CrownCom';

/**
 * Which mouse-button is used for click?
 */
export let PointerType;
/**
 * Which click action?
 */

(function (PointerType) {
  PointerType["PRIMARY"] = "PRIMARY";
  PointerType["SECONDARY"] = "SECONDARY";
  PointerType["MIDDLE"] = "MIDDLE";
})(PointerType || (PointerType = {}));

export let PointerActionType;
/**
 * Severity level for logging
 */

(function (PointerActionType) {
  PointerActionType["CLICKED"] = "CLICKED";
  PointerActionType["DOWN"] = "DOWN";
  PointerActionType["UP"] = "UP";
})(PointerActionType || (PointerActionType = {}));

export let LogLevel;
/**
 * Editor installation type
 */

(function (LogLevel) {
  LogLevel["INFO"] = "INFO";
  LogLevel["WARNING"] = "WARNING";
  LogLevel["ERROR"] = "ERROR";
})(LogLevel || (LogLevel = {}));

export let EditorType; // tslint:disable:no-any

/**
 * Access data via the ViewCrown interface.
 * Connection via websockets or similar is abstracted from the user (CrownConnection/CrownProtocol).
 *
 * Clients set public onXYZ functions in order to receive and handle Crown-View events.
 * Use the remaining public functions to send specific data to the host.
 */

(function (EditorType) {
  EditorType["INSTALL"] = "INSTALL";
  EditorType["UNINSTALL"] = "UNINSTALL";
})(EditorType || (EditorType = {}));

export class CrownView {
  /**
   * Creates a connection with the provided authority.
   * In detail the following steps are performed:
   * 1. Establish a coonection using the provided CrownConnection
   * 2. Create a View.Present handle (function call 'View/Present/create')
   * 3. Set the provided _viewID (function call 'View/Present/setID')
   * 4. Register for OnPresent events (function call 'View/Present/register')
   * 5. Call onConnected callback
   * 6. Push all incoming OnPresent payloads to the user
   *
   * @param _connection Use CrownConnectionMsgPack for performant com. Use CrownConnectionJson for human readable com.
   * @param _protocol port and ip address e.g. 127.0.0.1:80
   * @param _viewID the id which is used by the app to name the specific viewer instance
   */
  constructor(_connection, _protocol, _viewID) {
    this._onConnectionChangedCallbacks = new Array();
    this._onPresentStartCallbacks = new Array();
    this._onPresentEndCallbacks = new Array();
    this._onPresentAddCallbacks = new Array();
    this._onPresentClearCallbacks = new Array();
    this._onPresentRemoveCallbacks = new Array();
    this._onPresentUpdateDecorationCallbacks = new Array();
    this._onPresentUpdateTransformCallbacks = new Array();
    this._onPresentCameraCallbacks = new Array();
    this._onPresentEditorCallbacks = new Array();
    this._connection = _connection;
    this._protocol = _protocol;
    this._viewID = _viewID;

    // if _handleInitData was successful the onmessage property will be overwritten inside the _handleInitData method
    // so that it is called only once
    this._connection.setOnOpen(async () => {
      await this._createViewPresentCrownInstance();

      this._setupViewConnection();
    });

    this._connection.setOnClose(() => this._onConnectionChangedCallbacks.forEach(func => func(false)));

    this._connection.open();
  }
  /**
   * Adds a connectectionChnaged callback which is called after the connection has been established or interrupted.
   * Useful if device connection status is of interest.
   */


  onConnectionChanged(func) {
    this._onConnectionChangedCallbacks.push(func);
  }
  /**
   * Adds a PrensentStart callback which is called before presentation starts.
   * Called prior onPresentAdd, onPresentClear, onPresentAddRemove, onPresentEditor.
   */


  onPresentStart(func) {
    this._onPresentStartCallbacks.push(func);
  }
  /**
   * Adds a PrensentEnd callback which is called after presentation is completed.
   * Called after onPresentAdd, onPresentClear, onPresentAddRemove, onPresentEditor.
   */


  onPresentEnd(func) {
    this._onPresentEndCallbacks.push(func);
  }
  /**
   * Adds a Callback for adding iconics with an optional decoration to the scene.
   * Clients have to check if decoration is a valid object.
   *
   * @param  {(type:string,iconics:Array<CrownDataDesc>,iconicId:string,decorations:Array<CrownDataDesc>)=>void} func callback called if a iconic was added.
   * where the parameters are:
   * type: The manifested type of View.Present.Add (HEIGHTMAP, IMAGE, PIXEL_REGION, POINT_CLOUD, SHAPE, TEXT)
   * iconics: An array of iconics which are to be displayed
   * iconicId: This id is used to identify and change an iconic after it is displayed (e.g. remove)
   * decorations: Define additional drawing properties (e.g. colors)
   *
   */


  onPresentAdd(func) {
    this._onPresentAddCallbacks.push(func);
  }
  /**
   * Adds a Clear callback for clearing iconics from the scene.
   */


  onPresentClear(func) {
    this._onPresentClearCallbacks.push(func);
  }
  /**
   * Adds a remove callback for certain objects from the scene.
   *
   * @param  {(iconicId:string)=>void} func callback called to remove iconic with the iconicId
   */


  onPresentRemove(func) {
    this._onPresentRemoveCallbacks.push(func);
  }
  /**
   * Adds an editor callback of the specified type and iconic
   *
   * @param  {(type:EditorType,iconicId:string)=>void} func callback called with the parameters:
   * type: The manifested type of View.Present.Editor.EditorType (INSTALL, UNINSTALL)
   * iconicId: The id on which the editor is applied on
   */


  onPresentEditor(func) {
    this._onPresentEditorCallbacks.push(func);
  }
  /**
   * Adds a camera callback which is called if the camera settings are to be changed
   *
   * @param  {(camera: CrownViewObjectDataCameraDesc)=>void} func callback called with the parameters:
   * camera: Camera settings
   */


  onPresentCamera(func) {
    this._onPresentCameraCallbacks.push(func);
  }
  /**
   * Adds an updateDecoration callback
   *
   * @param  {(iconicId:string, decoration:CrownDataDesc)=>void} func callback called with the parameters:
   * iconicId: The id on which the editor is applied on
   * decoration: decoration to be updated for the specified iconic
   */


  onPresentUpdateDecoration(func) {
    this._onPresentUpdateDecorationCallbacks.push(func);
  }
  /**
   * Adds an updateTransform callback
   *
   * @param  {(iconicId:string, transform:any)=>void} func callback called with the parameters:
   * iconicId: The id on which the editor is applied on
   * transform: The transform matrix to be set
   */


  onPresentUpdateTransform(func) {
    this._onPresentUpdateTransformCallbacks.push(func);
  }
  /**
   * Calls the host's OnPointer event.
   * @param iconicId the iconic on which the pointer event occurred
   * @param pointerActionType the event type
   * @param pointerType type of the pointer which caused the event to be fired
   */


  notifyPointerEvent(iconicId, pointerActionType, pointerType) {
    const call = {
      header: {
        type: 'FunctionCall',
        clientId: 0,
        function: 'View/Present/notifyPointerEvent'
      },
      data: {
        handle: {
          type: 'handle',
          id: this._crownHdl
        },
        iconicId,
        pointerType,
        pointerActionType
      }
    };

    this._connection.send(this._protocol.encode(call));
  }
  /**
   * Calls the host's OnChange event.
   * @param iconicId  the iconic which changed (e.g. after being edited)
   * @param iconic the iconic object after the change which is reflected back to the host
   */


  notifyIconicChangedEvent(iconicId, iconic) {
    const call = {
      header: {
        type: 'FunctionCall',
        clientId: 0,
        function: 'View/Present/notifyIconicChangedEvent'
      },
      data: {
        handle: {
          type: 'handle',
          id: this._crownHdl
        },
        iconicId,
        iconic: {
          type: 'object',
          data: iconic
        }
      }
    };

    this._connection.send(this._protocol.encode(call));
  }
  /**
   * Output log-messages on the host.
   * @param level the severity level
   * @param msg the message to be logged
   */


  logMessage(level, message) {
    const call = {
      header: {
        type: 'FunctionCall',
        clientId: 0,
        function: 'View/Present/logMessage'
      },
      data: {
        handle: {
          type: 'handle',
          id: this._crownHdl
        },
        level,
        message
      }
    };

    this._connection.send(this._protocol.encode(call));
  }
  /**
   * Create an instance handle for the View.Present Crown.
   */


  async _createViewPresentCrownInstance() {
    const call = CrownCallFactory.createFunctionCall('View/Present/create');
    const ret = await this._sendSync(call);

    if (CrownCom.checkResponseHeader(ret)) {
      this._crownHdl = ret.data.handle.id;
    }
  }
  /**
   * Handle response to create.
   * Store the Crown handle, setID and register to OnPresent.
   * @param msg
   */


  async _setupViewConnection() {
    const callSetID = CrownCallFactory.createFunctionCall('View/Present/setID', CrownCallFactory.createDataSetViewId(this._crownHdl, this._viewID));
    await this._sendSync(callSetID);
    let call = CrownCallFactory.createFunctionCall('View/Present/register', CrownCallFactory.createDataRegisterEvent('OnPresentAssured', this._crownHdl), CrownCallFactory.createOptionMessageQueue('HIGH', 0, 'OLDEST'));
    let ret = false;
    let res = await this._sendSync(call);

    if (CrownCom.checkFunctionCallResponse(res)) {
      // could register to OnPresentAssured (new api)
      call = CrownCallFactory.createFunctionCall('View/Present/register', CrownCallFactory.createDataRegisterEvent('OnPresentLive', this._crownHdl), CrownCallFactory.createOptionMessageQueue('HIGH', 0, 'OLDEST'));
      res = await this._sendSync(call);
      ret = CrownCom.checkFunctionCallResponse(res); // must work since live and assured are a pair

      if (!ret) {
        console.error('Could not register to "OnPresentLive" event'); // tslint:disable-line:no-console
      }
    } else {
      call = CrownCallFactory.createFunctionCall('View/Present/register', CrownCallFactory.createDataRegisterEvent('OnPresent', this._crownHdl));
      res = await this._sendSync(call);
      ret = CrownCom.checkFunctionCallResponse(res);
    }

    this._connection.setOnData(d => this._handlePayloadData(d));

    this._onConnectionChangedCallbacks.forEach(func => func(ret));
  }
  /**
   * Handle the onPresent event and its payload
   * @param msg
   */


  _handlePayloadData(data) {
    const obj = this._protocol.decode(data);

    if (CrownCom.checkResponseHeader(obj)) {
      const vos = obj.data;

      if (vos != null && vos.viewObject != null && vos.viewObject.length > 0) {
        this._onPresentStartCallbacks.forEach(func => func());

        vos.viewObject.forEach(vo => {
          switch (vo.data.class) {
            case 'View.Present.Add':
              const add = vo.data.data;

              this._onPresentAddCallbacks.forEach(func => func(add.Type, add.Iconics, add.IconicId, add.ParentId, add.Decorations));

              break;

            case 'View.Present.Clear':
              this._onPresentClearCallbacks.forEach(func => func());

              break;

            case 'View.Present.Editor':
              const editor = vo.data.data;

              this._onPresentEditorCallbacks.forEach(func => func(editor.Type, editor.IconicId));

              break;

            case 'View.Present.Remove':
              const remove = vo.data.data;

              this._onPresentRemoveCallbacks.forEach(func => func(remove.IconicId));

              break;

            case 'View.Present.UpdateDecoration':
              const updateDeco = vo.data.data;

              this._onPresentUpdateDecorationCallbacks.forEach(func => func(updateDeco.IconicId, updateDeco.Decoration));

              break;

            case 'View.Present.UpdateTransform':
              const updateTransform = vo.data.data;

              this._onPresentUpdateTransformCallbacks.forEach(func => func(updateTransform.IconicId, updateTransform.Transform));

              break;

            case 'View.Present.UpdateCamera':
              const updateCamera = vo.data.data;
              const camera = updateCamera.Camera.data;

              this._onPresentCameraCallbacks.forEach(func => func(camera.PositionSet ? camera.Position : undefined, camera.TargetSet ? camera.Target : undefined, camera.UpSet ? camera.Up : undefined));

              break;

            default:
              console.error('CrownViewAPI: unknown class type:' + vo.data.class); // tslint:disable-line:no-console

              break;
          }
        });

        this._onPresentEndCallbacks.forEach(func => func());
      }
    }
  }

  async _sendSync(call) {
    this._connection.send(this._protocol.encode(call));

    return new Promise(res => {
      this._connection.setOnData(data => {
        res(this._protocol.decode(data));
      });
    });
  }

}
//# sourceMappingURL=CrownView.js.map